import { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/layout/Layout';
import { DocsSidebar } from '@/components/docs/DocsSidebar';
import { CodeBlock } from '@/components/docs/CodeBlock';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Lightbulb, 
  Info,
  ArrowRight,
  CheckCircle2,
  FileCode,
  Database,
  Shield,
  Layers
} from 'lucide-react';

export default function Documentation() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const contentRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-section]');
      let current = 'getting-started';
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 150) current = section.id;
      });
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <div className="flex gap-4 lg:gap-8">
            <DocsSidebar activeSection={activeSection} onSectionClick={scrollToSection} />

            <main ref={contentRef} className="flex-1 min-w-0 max-w-4xl">
              <div className="mb-8 md:mb-12">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Data Standards Documentation</h1>
                <p className="text-base md:text-lg text-muted-foreground">
                  Learn how to use, implement, and contribute to automotive industry data standards.
                </p>
              </div>

              {/* Getting Started */}
              <section id="getting-started" data-section className="mb-10 md:mb-16 scroll-mt-24">
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6 pb-2 border-b border-border">Getting Started</h2>
                
                <div id="getting-started-overview" data-section className="mb-8 scroll-mt-24">
                  <h3 className="text-xl font-semibold text-foreground mb-4">Overview</h3>
                  <p className="text-muted-foreground mb-4">
                    Our data standards provide a unified schema for automotive industry data exchange. 
                    Each standard defines the structure, field types, and validation rules for consistent data handling across systems.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card className="border-border">
                      <CardContent className="pt-6">
                        <Database className="h-8 w-8 text-primary mb-3" />
                        <h4 className="font-semibold text-foreground mb-2">Structured Schemas</h4>
                        <p className="text-sm text-muted-foreground">JSON Schema definitions for all data types</p>
                      </CardContent>
                    </Card>
                    <Card className="border-border">
                      <CardContent className="pt-6">
                        <Shield className="h-8 w-8 text-primary mb-3" />
                        <h4 className="font-semibold text-foreground mb-2">Validated Data</h4>
                        <p className="text-sm text-muted-foreground">Built-in validation ensures data integrity</p>
                      </CardContent>
                    </Card>
                    <Card className="border-border">
                      <CardContent className="pt-6">
                        <Layers className="h-8 w-8 text-primary mb-3" />
                        <h4 className="font-semibold text-foreground mb-2">Versioned</h4>
                        <p className="text-sm text-muted-foreground">Semantic versioning for backward compatibility</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div id="getting-started-browse" data-section className="mb-8 scroll-mt-24">
                  <h3 className="text-xl font-semibold text-foreground mb-4">Browsing Standards</h3>
                  <ol className="space-y-4">
                    <li className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Visit the Data Standards page</p>
                        <p className="text-sm text-muted-foreground">Browse all available standards organized by category.</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Select a standard</p>
                        <p className="text-sm text-muted-foreground">View the schema definition, field descriptions, and examples.</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Validate your data</p>
                        <p className="text-sm text-muted-foreground">Use the built-in validator to test your JSON against the schema.</p>
                      </div>
                    </li>
                  </ol>
                  <div className="mt-6">
                    <Button asChild>
                      <Link to="/api-standards">
                        Browse Standards
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </section>

              {/* Schema Reference */}
              <section id="schema-reference" data-section className="mb-10 md:mb-16 scroll-mt-24">
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6 pb-2 border-b border-border">Schema Reference</h2>
                
                <div id="schema-reference-structure" data-section className="mb-8 scroll-mt-24">
                  <h3 className="text-xl font-semibold text-foreground mb-4">Schema Structure</h3>
                  <p className="text-muted-foreground mb-4">
                    Each data standard is defined using JSON Schema (draft-07). Here's an example of a typical schema structure:
                  </p>
                  <CodeBlock 
                    language="json" 
                    title="Example Schema" 
                    showLineNumbers
                    code={`{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "title": "Vehicle",
  "type": "object",
  "required": ["vin", "make", "model", "year"],
  "properties": {
    "vin": {
      "type": "string",
      "pattern": "^[A-HJ-NPR-Z0-9]{17}$",
      "description": "17-character Vehicle Identification Number"
    },
    "make": {
      "type": "string",
      "description": "Vehicle manufacturer"
    },
    "model": {
      "type": "string",
      "description": "Vehicle model name"
    },
    "year": {
      "type": "integer",
      "minimum": 1900,
      "maximum": 2100,
      "description": "Model year"
    }
  }
}`} 
                  />
                </div>

                <div id="schema-reference-types" data-section className="mb-6 md:mb-8 scroll-mt-24">
                  <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">Field Types</h3>
                  
                  {/* Desktop table - hidden on mobile */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 font-medium text-foreground">Type</th>
                          <th className="text-left py-3 px-4 font-medium text-foreground">Description</th>
                          <th className="text-left py-3 px-4 font-medium text-foreground">Example</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-border">
                          <td className="py-3 px-4"><Badge variant="outline">string</Badge></td>
                          <td className="py-3 px-4 text-muted-foreground">Text values</td>
                          <td className="py-3 px-4 text-muted-foreground font-mono text-xs">"Toyota"</td>
                        </tr>
                        <tr className="border-b border-border">
                          <td className="py-3 px-4"><Badge variant="outline">integer</Badge></td>
                          <td className="py-3 px-4 text-muted-foreground">Whole numbers</td>
                          <td className="py-3 px-4 text-muted-foreground font-mono text-xs">2024</td>
                        </tr>
                        <tr className="border-b border-border">
                          <td className="py-3 px-4"><Badge variant="outline">number</Badge></td>
                          <td className="py-3 px-4 text-muted-foreground">Decimal numbers</td>
                          <td className="py-3 px-4 text-muted-foreground font-mono text-xs">29999.99</td>
                        </tr>
                        <tr className="border-b border-border">
                          <td className="py-3 px-4"><Badge variant="outline">boolean</Badge></td>
                          <td className="py-3 px-4 text-muted-foreground">True/false values</td>
                          <td className="py-3 px-4 text-muted-foreground font-mono text-xs">true</td>
                        </tr>
                        <tr className="border-b border-border">
                          <td className="py-3 px-4"><Badge variant="outline">array</Badge></td>
                          <td className="py-3 px-4 text-muted-foreground">List of items</td>
                          <td className="py-3 px-4 text-muted-foreground font-mono text-xs">["red", "blue"]</td>
                        </tr>
                        <tr className="border-b border-border">
                          <td className="py-3 px-4"><Badge variant="outline">object</Badge></td>
                          <td className="py-3 px-4 text-muted-foreground">Nested structure</td>
                          <td className="py-3 px-4 text-muted-foreground font-mono text-xs">{`{...}`}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Mobile cards - visible only on mobile */}
                  <div className="md:hidden space-y-3">
                    {[
                      { type: 'string', description: 'Text values', example: '"Toyota"' },
                      { type: 'integer', description: 'Whole numbers', example: '2024' },
                      { type: 'number', description: 'Decimal numbers', example: '29999.99' },
                      { type: 'boolean', description: 'True/false values', example: 'true' },
                      { type: 'array', description: 'List of items', example: '["red", "blue"]' },
                      { type: 'object', description: 'Nested structure', example: '{...}' },
                    ].map((field) => (
                      <div key={field.type} className="border border-border rounded-lg p-3 flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <Badge variant="outline" className="mb-1">{field.type}</Badge>
                          <p className="text-sm text-muted-foreground">{field.description}</p>
                        </div>
                        <code className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded shrink-0">
                          {field.example}
                        </code>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* API Reference */}
              <section id="api-reference" data-section className="mb-10 md:mb-16 scroll-mt-24">
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6 pb-2 border-b border-border">API Reference</h2>
                
                <div id="api-reference-authentication" data-section className="mb-8 scroll-mt-24">
                  <h3 className="text-xl font-semibold text-foreground mb-4">Authentication</h3>
                  <p className="text-muted-foreground mb-4">
                    The Standards API supports both public and authenticated access. Authenticated users get higher rate limits.
                  </p>
                  <CodeBlock 
                    language="bash" 
                    title="Optional Authentication" 
                    showLineNumbers
                    code={`# Public access (no auth required)
curl https://fxezreosgghqhhgmvbgw.supabase.co/functions/v1/standards-api/standards

# Authenticated access (higher rate limits)
curl -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  https://fxezreosgghqhhgmvbgw.supabase.co/functions/v1/standards-api/standards`} 
                  />
                </div>

                <div id="api-reference-rate-limits" data-section className="mb-8 scroll-mt-24">
                  <h3 className="text-xl font-semibold text-foreground mb-4">Rate Limits</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 font-medium text-foreground">Access Type</th>
                          <th className="text-left py-3 px-4 font-medium text-foreground">Rate Limit</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-border">
                          <td className="py-3 px-4 text-muted-foreground">Public (no auth)</td>
                          <td className="py-3 px-4"><Badge variant="outline">60 requests/min</Badge></td>
                        </tr>
                        <tr className="border-b border-border">
                          <td className="py-3 px-4 text-muted-foreground">Authenticated</td>
                          <td className="py-3 px-4"><Badge variant="outline">300 requests/min</Badge></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <Alert className="mt-4 border-primary/20 bg-primary/5">
                    <Info className="h-4 w-4 text-primary" />
                    <AlertDescription className="text-foreground">
                      Rate limit headers are included in every response: <code className="text-xs">X-RateLimit-Limit</code>, <code className="text-xs">X-RateLimit-Remaining</code>, <code className="text-xs">X-RateLimit-Reset</code>
                    </AlertDescription>
                  </Alert>
                </div>

                <div id="api-reference-endpoints" data-section className="mb-8 scroll-mt-24">
                  <h3 className="text-xl font-semibold text-foreground mb-4">Endpoints</h3>
                  <div className="space-y-4">
                    <Card className="border-border">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">GET</Badge>
                          <code className="text-sm font-mono">/standards</code>
                        </div>
                        <p className="text-sm text-muted-foreground">List all active data standards</p>
                      </CardContent>
                    </Card>
                    <Card className="border-border">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">GET</Badge>
                          <code className="text-sm font-mono">/standards/:slug</code>
                        </div>
                        <p className="text-sm text-muted-foreground">Get a single standard with full schema, example, and fields</p>
                      </CardContent>
                    </Card>
                    <Card className="border-border">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">GET</Badge>
                          <code className="text-sm font-mono">/standards/:slug/schema</code>
                        </div>
                        <p className="text-sm text-muted-foreground">Get just the JSON Schema for a standard</p>
                      </CardContent>
                    </Card>
                    <Card className="border-border">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">GET</Badge>
                          <code className="text-sm font-mono">/standards/:slug/typescript</code>
                        </div>
                        <p className="text-sm text-muted-foreground">Generate TypeScript type definitions</p>
                      </CardContent>
                    </Card>
                    <Card className="border-border">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">GET</Badge>
                          <code className="text-sm font-mono">/lookups/:group</code>
                        </div>
                        <p className="text-sm text-muted-foreground">Get lookup values by group name</p>
                      </CardContent>
                    </Card>
                    <Card className="border-border">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">POST</Badge>
                          <code className="text-sm font-mono">/validate</code>
                        </div>
                        <p className="text-sm text-muted-foreground">Validate JSON data against a standard schema</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div id="api-reference-examples" data-section className="mb-8 scroll-mt-24">
                  <h3 className="text-xl font-semibold text-foreground mb-4">Code Examples</h3>
                  <CodeBlock 
                    language="typescript" 
                    title="Fetch and Validate Data" 
                    showLineNumbers
                    code={`const API_BASE = "https://fxezreosgghqhhgmvbgw.supabase.co/functions/v1/standards-api";

// Fetch a standard's schema
const response = await fetch(\`\${API_BASE}/standards/vehicle\`);
const { data } = await response.json();

// Validate data against a standard
const validation = await fetch(\`\${API_BASE}/validate\`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    standardSlug: "vehicle",
    data: { vin: "1HGBH41JXMN109186", make: "Honda" }
  })
});
const result = await validation.json();
// { valid: true/false, errors: [...] }`} 
                  />
                </div>
              </section>

              {/* Implementation */}
              <section id="implementation" data-section className="mb-10 md:mb-16 scroll-mt-24">
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6 pb-2 border-b border-border">Implementation</h2>
                
                <div id="implementation-typescript" data-section className="mb-8 scroll-mt-24">
                  <h3 className="text-xl font-semibold text-foreground mb-4">TypeScript Example</h3>
                  <p className="text-muted-foreground mb-4">
                    Use our schemas to generate TypeScript interfaces for type-safe data handling:
                  </p>
                  <CodeBlock 
                    language="typescript" 
                    title="vehicle.types.ts" 
                    showLineNumbers
                    code={`interface Vehicle {
  vin: string;
  make: string;
  model: string;
  year: number;
  mileage?: number;
  color?: string;
  price?: number;
}

// Example usage
const vehicle: Vehicle = {
  vin: "1HGBH41JXMN109186",
  make: "Honda",
  model: "Accord",
  year: 2024
};`} 
                  />
                </div>

                <div id="implementation-validation" data-section className="mb-8 scroll-mt-24">
                  <h3 className="text-xl font-semibold text-foreground mb-4">Validation</h3>
                  <p className="text-muted-foreground mb-4">
                    Validate data against schemas using libraries like AJV:
                  </p>
                  <CodeBlock 
                    language="typescript" 
                    title="validate.ts" 
                    showLineNumbers
                    code={`import Ajv from "ajv";
import vehicleSchema from "./schemas/vehicle.json";

const ajv = new Ajv();
const validate = ajv.compile(vehicleSchema);

const data = {
  vin: "1HGBH41JXMN109186",
  make: "Honda",
  model: "Accord",
  year: 2024
};

if (validate(data)) {
  console.log("Valid vehicle data!");
} else {
  console.error(validate.errors);
}`} 
                  />
                  <Alert className="mt-4 border-primary/20 bg-primary/5">
                    <Lightbulb className="h-4 w-4 text-primary" />
                    <AlertDescription className="text-foreground">
                      <strong>Tip:</strong> Use our built-in validator on the Data Standards page to test your data without writing code.
                    </AlertDescription>
                  </Alert>
                </div>
              </section>

              {/* Contributing */}
              <section id="contributing" data-section className="mb-10 md:mb-16 scroll-mt-24">
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6 pb-2 border-b border-border">Contributing</h2>
                
                <div id="contributing-propose" data-section className="mb-8 scroll-mt-24">
                  <h3 className="text-xl font-semibold text-foreground mb-4">Proposing New Standards</h3>
                  <p className="text-muted-foreground mb-4">
                    Have a data standard that would benefit the automotive industry? Here's how to propose one:
                  </p>
                  <ol className="space-y-4">
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">1</span>
                      <div>
                        <p className="font-medium text-foreground">Draft your schema</p>
                        <p className="text-sm text-muted-foreground">Create a JSON Schema following our structure guidelines.</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">2</span>
                      <div>
                        <p className="font-medium text-foreground">Include documentation</p>
                        <p className="text-sm text-muted-foreground">Provide field descriptions, examples, and use cases.</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">3</span>
                      <div>
                        <p className="font-medium text-foreground">Submit for review</p>
                        <p className="text-sm text-muted-foreground">Contact our team to submit your proposal for review.</p>
                      </div>
                    </li>
                  </ol>
                </div>
              </section>

              {/* FAQ */}
              <section id="faq" data-section className="mb-10 md:mb-16 scroll-mt-24">
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6 pb-2 border-b border-border">FAQ</h2>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-0" className="border-border">
                    <AccordionTrigger className="text-foreground">What are data standards?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Data standards are predefined schemas that define the structure, types, and validation rules for data. 
                      They ensure consistency when exchanging data between different systems in the automotive industry.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-1" className="border-border">
                    <AccordionTrigger className="text-foreground">How do I validate my data?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      You can use our built-in validator on the Data Standards page, or integrate the JSON schemas into your 
                      own systems using validation libraries like AJV for JavaScript/TypeScript.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-2" className="border-border">
                    <AccordionTrigger className="text-foreground">Are the schemas versioned?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Yes, all schemas use semantic versioning (e.g., 1.0.0). We maintain backward compatibility within 
                      major versions and document all changes in the changelog.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-3" className="border-border">
                    <AccordionTrigger className="text-foreground">Can I use these standards in my own systems?</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Absolutely! Our data standards are designed to be adopted across the industry. You can download 
                      the schemas and integrate them into your data pipelines, APIs, and applications.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </section>

              {/* CTA */}
              <section className="bg-muted/50 rounded-xl p-5 md:p-8 text-center">
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-3">Ready to Explore?</h2>
                <p className="text-muted-foreground mb-6">Browse our collection of automotive data standards.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild>
                    <Link to="/api-standards">
                      View Data Standards
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/support">
                      Get Help
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </section>
            </main>

          </div>
        </div>
      </div>
    </Layout>
  );
}
